import {
  AbilityBuilder,
  AbilityClass,
  createAliasResolver,
  PureAbility,
} from "@casl/ability";
import { InjectionKey } from "vue";
import { ABILITY_TOKEN } from "@casl/vue";
import { intersection } from "lodash";

type Actions =
  | "create"
  | "read"
  | "update"
  | "delete"
  | "fullAccess"
  | "modify"
  | "force";

export const subjectsArray = [
  // ---------------------------------- MES -----------------------------------
  "Handheld",
  "Login",
  "MfaSetup",
  "MfaVerification",
  "ChangePassword",
  "ForgotPassword",
  "Verification",
  "Api",
  "Articles",
  "Dashboards",
  "Devices",
  "Factories",
  "Home",
  "Error",
  "Forbidden",
  "Orders",
  "OrderDetails",
  "OrdersQueued",
  "My Profile",
  "Settings",
  "Planning",
  "Reports",
  "StickerTemplates",
  "Tasks",
  "Templates",
  "DefaultPlanning",
  "Tokens",
  "Users",
  "Photos",

  // ------------------------------- EE factory -------------------------------
  "EE Dashboard",
  "EE Tasks",
  "EE Batches",
  "EE Settings",
  "EE Devices",

  // ------------------------------- ZH factory -------------------------------
  "ZH Cockpits",
  "ZH Factory",
  "ZH PreparePP",
  "ZH Stelling PP",
  "ZH Dashboard",
  "ZH Settings",
  "ZH Devices",

  // ------------------------------- ZF factory -------------------------------
  "ZF AR",
  "ZF Articles",
  "ZF Assets",
  "ZF Cockpits",
  "ZF DashboardAssembly",
  "ZF DashboardAssemblyProgress",
  "ZF DashboardProgress",
  "ZF Dashboard Assembly",
  "ZF Factory",
  "ZF Luxe-Line Assembly",
  "ZF Panels",
  "ZF Passdoor Assembly",
  "ZF Powder Coating",
  "ZF PP",
  "ZF PreparePP",
  "ZF PreparePaintPP",
  "ZF Saw infeed",
  "ZF Saws optimizer",
  "ZF Settings",
  "ZF Spider",
  "ZF Spider 2",
  "ZF Spider 3",
  "ZF Tasks",
  "ZF Configuration",
  "ZF Assembly Line",

  // ---------------------------------- ZF Handheld -----------------------------------
  "ZF Handheld",
  "ZF Handheld Assembly Rubber",
  "ZF Handheld Assembly Stickers",
  "ZF Handheld Assembly EndCap",
  "ZF Handheld Assembly Bottom",
  "ZF Handheld Assembly Top",
  "ZF Handheld Assembly Special1",
  "ZF Handheld Assembly SpecialsOverview",
  "ZF Handheld Assembly SpecialsDetail",
  "ZF Handheld Assembly Special2",
  "ZF Handheld Assembly Collo",
  "ZF Handheld Assembly Packaging",
  "ZF Handheld Assembly Lintel",

  "ZF Handheld Stations Lintel PreparePaintPP",
  "ZF Handheld Stations Lintel PreparePP",

  // ---------------------------------- HF Handheld -----------------------------------
  "ZH Handheld Stations Mabo BufferPP",
  "ZH Handheld Infeed1",
  "ZH Handheld Infeed2",

  // ---------------------------------- Emmen Handheld --------------------------------
  "EE Handheld Profiling",
] as const;

export type Subjects = (typeof subjectsArray)[number];
export type AppAbility = PureAbility<[Actions, Subjects]>;
export const AppAbility = PureAbility as AbilityClass<AppAbility>;

export function getSubject(subject: Subjects): string {
  return subject;
}

export function getSubjectOrDefault(subject: string): Subjects {
  if (subjectsArray.findIndex((o) => o == subject) < 0) {
    return "Home";
  } else {
    return subject as Subjects;
  }
}

export function getSubjectFromString(
  subject: string | undefined,
): Subjects | undefined {
  if (subject == undefined) {
    return undefined;
  } else if (subjectsArray.findIndex((o) => o == subject) < 0) {
    return undefined;
  } else {
    return subject as Subjects;
  }
}

export function hasSalesGroup(groups: string[]): boolean {
  return groups.some((group) => group.toLowerCase() === "sales");
}

const resolveAction = createAliasResolver({
  fullAccess: ["create", "read", "update", "delete"],
  modify: ["read", "update"],
});

export default function defineRules(groups: string[]) {
  const { can, rules } = new AbilityBuilder(AppAbility);

  if (intersection(groups, ["Admin", "Superuser"]).length > 0) {
    can("force", "EE Batches");
    can("force", "EE Tasks");
  }

  // Admin has access to everything
  if (intersection(groups, ["Admin"]).length > 0) {
    can("fullAccess", "all");
    return rules;
  }

  // Universally available to any authenticated user
  can("read", "My Profile");

  // Common pages accessible by all regular roles
  const commonPages = [
    "Articles",
    "Factories",
    "Home",
    "Orders",
    "OrderDetails",
    "Planning",
    "Tasks",
    "Reports",
    "Dashboards",
  ];

  // ================================ EE Factory ================================
  if (intersection(groups, ["ee-operator"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    // EE factory resources to be added as needed
  }

  if (intersection(groups, ["ee-frontman"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    // EE factory resources to be added as needed
  }

  if (intersection(groups, ["ee-head-operator"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    // EE factory resources to be added as needed
  }

  if (intersection(groups, ["ee-head-production"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    // EE factory resources to be added as needed
  }

  if (intersection(groups, ["ee-work-planner"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    // EE factory resources to be added as needed
  }

  // ================================ ZH Factory ================================
  if (intersection(groups, ["zh-operator"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    can("read", "ZH Cockpits");
    can("read", "ZH Factory");
    can("read", "ZH PreparePP");
    can("read", "ZH Stelling PP");
    can("read", "Handheld");
    can("read", "ZF Handheld");
    can("read", "ZH Handheld Infeed1");
    can("read", "ZH Handheld Infeed2");
    can("read", "ZH Handheld Stations Mabo BufferPP");
  }

  if (intersection(groups, ["zh-frontman"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    can("read", "ZH Cockpits");
    can("read", "ZH Factory");
    can("read", "ZH Settings");
    can("read", "ZH Stelling PP");
    can("read", "Handheld");
    can("read", "ZF Handheld");
    can("read", "ZH Handheld Infeed1");
    can("read", "ZH Handheld Infeed2");
    can("read", "ZH Handheld Stations Mabo BufferPP");
  }

  if (intersection(groups, ["zh-head-operator"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    can("modify", "ZH Cockpits");
    can("modify", "ZH Factory");
    can("modify", "ZH PreparePP");
    can("modify", "ZH Settings");
    can("modify", "ZH Stelling PP");
    can("fullAccess", "Tasks");
    can("read", "Handheld");
    can("read", "ZF Handheld");
    can("modify", "ZH Handheld Infeed1");
    can("modify", "ZH Handheld Infeed2");
    can("modify", "ZH Handheld Stations Mabo BufferPP");
  }

  if (intersection(groups, ["zh-head-production"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    can("modify", "ZH Cockpits");
    can("modify", "ZH Factory");
    can("modify", "ZH PreparePP");
    can("read", "ZH Settings");
    can("modify", "ZH Stelling PP");
    can("fullAccess", "Tasks");
    can("read", "Handheld");
    can("read", "ZF Handheld");
    can("modify", "ZH Handheld Infeed1");
    can("modify", "ZH Handheld Infeed2");
    can("modify", "ZH Handheld Stations Mabo BufferPP");
  }

  if (intersection(groups, ["zh-work-planner"]).length > 0) {
    commonPages.forEach((page) => can("modify", page));
    can("read", "ZH Cockpits");
    can("read", "ZH Factory");
    can("modify", "ZH PreparePP");
    can("read", "ZH Settings");
    can("read", "ZH Handheld Infeed1");
    can("read", "ZH Handheld Infeed2");
    can("read", "ZH Handheld Stations Mabo BufferPP");
    can("fullAccess", "Tasks");
    can("read", "Handheld");
    can("read", "ZF Handheld");
  }

  // ================================ ZF Factory ================================
  if (intersection(groups, ["zf-operator"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    can("modify", "ZF AR");
    can("modify", "ZF Articles");
    can("read", "ZF Assets");
    can("read", "ZF Cockpits");
    can("read", "ZF Factory");
    can("read", "ZF Panels");
    can("modify", "ZF PreparePP");
    can("modify", "ZF Saws optimizer");
    can("modify", "ZF Saw infeed");
    can("read", "ZF Spider");
    can("read", "ZF Spider 2");
    can("read", "ZF Spider 3");
    can("modify", "ZF Tasks");
    can("read", "ZF DashboardAssembly");
    can("read", "ZF DashboardAssemblyProgress");
    can("read", "ZF DashboardProgress");
    can("read", "ZF Luxe-Line Assembly");
    can("read", "ZF Passdoor Assembly");
    can("read", "ZF Powder Coating");
    can("read", "ZF PP");
    can("read", "ZF PreparePaintPP");
    can("read", "Handheld");
    can("read", "ZF Handheld");
    can("fullAccess", "ZF Handheld Assembly Rubber");
    can("fullAccess", "ZF Handheld Assembly Stickers");
    can("fullAccess", "ZF Handheld Assembly EndCap");
    can("fullAccess", "ZF Handheld Assembly Bottom");
    can("fullAccess", "ZF Handheld Assembly Top");
    can("fullAccess", "ZF Handheld Assembly Special1");
    can("fullAccess", "ZF Handheld Assembly SpecialsOverview");
    can("fullAccess", "ZF Handheld Assembly SpecialsDetail");
    can("fullAccess", "ZF Handheld Assembly Special2");
    can("fullAccess", "ZF Handheld Assembly Collo");
    can("fullAccess", "ZF Handheld Assembly Packaging");
    can("fullAccess", "ZF Handheld Assembly Lintel");
    can("modify", "ZF Handheld Stations Lintel PreparePaintPP");
    can("modify", "ZF Handheld Stations Lintel PreparePP");
    can("modify", "ZF Assembly Line");
  }

  if (intersection(groups, ["zf-frontman"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    can("read", "ZF AR");
    can("read", "ZF Articles");
    can("read", "ZF Cockpits");
    can("read", "ZF Factory");
    can("read", "ZF Panels");
    can("read", "ZF Settings");
    can("read", "ZF Tasks");
    can("read", "Handheld");
    can("read", "ZF Handheld");
    can("read", "ZF Assembly Line");
    can("fullAccess", "Tasks");
  }

  if (intersection(groups, ["zf-head-operator"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    can("fullAccess", "ZF AR");
    can("fullAccess", "ZF Articles");
    can("modify", "ZF Assets");
    can("modify", "ZF Cockpits");
    can("modify", "ZF Factory");
    can("modify", "ZF Panels");
    can("fullAccess", "ZF PreparePP");
    can("fullAccess", "ZF Saws optimizer");
    can("fullAccess", "ZF Saw infeed");
    can("modify", "ZF Settings");
    can("modify", "ZF Spider");
    can("modify", "ZF Spider 2");
    can("modify", "ZF Spider 3");
    can("fullAccess", "ZF Tasks");
    can("modify", "ZF DashboardAssembly");
    can("modify", "ZF DashboardAssemblyProgress");
    can("modify", "ZF DashboardProgress");
    can("modify", "ZF Luxe-Line Assembly");
    can("modify", "ZF Passdoor Assembly");
    can("modify", "ZF Powder Coating");
    can("modify", "ZF PP");
    can("fullAccess", "ZF PreparePaintPP");
    can("modify", "Handheld");
    can("modify", "ZF Handheld");
    can("fullAccess", "ZF Handheld Assembly Rubber");
    can("fullAccess", "ZF Handheld Assembly Stickers");
    can("fullAccess", "ZF Handheld Assembly EndCap");
    can("fullAccess", "ZF Handheld Assembly Bottom");
    can("fullAccess", "ZF Handheld Assembly Top");
    can("fullAccess", "ZF Handheld Assembly Special1");
    can("fullAccess", "ZF Handheld Assembly SpecialsOverview");
    can("fullAccess", "ZF Handheld Assembly SpecialsDetail");
    can("fullAccess", "ZF Handheld Assembly Special2");
    can("fullAccess", "ZF Handheld Assembly Collo");
    can("fullAccess", "ZF Handheld Assembly Packaging");
    can("fullAccess", "ZF Handheld Assembly Lintel");
    can("fullAccess", "ZF Handheld Stations Lintel PreparePaintPP");
    can("fullAccess", "ZF Handheld Stations Lintel PreparePP");
    can("fullAccess", "ZF Assembly Line");
    can("fullAccess", "Tasks");
  }

  if (intersection(groups, ["zf-head-production"]).length > 0) {
    commonPages.forEach((page) => can("read", page));
    can("modify", "ZF AR");
    can("modify", "ZF Articles");
    can("read", "ZF Assets");
    can("modify", "ZF Cockpits");
    can("modify", "ZF Factory");
    can("read", "ZF Panels");
    can("fullAccess", "ZF PreparePP");
    can("fullAccess", "ZF Saws optimizer");
    can("fullAccess", "ZF Saw infeed");
    can("read", "ZF Settings");
    can("modify", "ZF Spider");
    can("modify", "ZF Spider 2");
    can("modify", "ZF Spider 3");
    can("fullAccess", "ZF Tasks");
    can("modify", "ZF DashboardAssembly");
    can("modify", "ZF DashboardAssemblyProgress");
    can("modify", "ZF DashboardProgress");
    can("modify", "ZF Luxe-Line Assembly");
    can("read", "ZF Passdoor Assembly");
    can("modify", "ZF Powder Coating");
    can("read", "ZF PP");
    can("fullAccess", "ZF PreparePaintPP");
    can("fullAccess", "ZF Assembly Line");
    can("fullAccess", "Tasks");
  }

  if (intersection(groups, ["zf-work-planner"]).length > 0) {
    commonPages.forEach((page) => can("modify", page));
    can("read", "ZF Factory");
    can("read", "ZF Cockpits");
    can("read", "ZF AR");
    can("read", "ZF Articles");
    can("read", "ZF Tasks");
    can("read", "ZF Assembly Line");
  }

  if (hasSalesGroup(groups)) {
    can("read", "Photos");
  }

  // Emmen Edisonstraat
  if (intersection(groups, ["ee-work-planner"]).length > 0) {
    can("read", "EE Batches");
    can("read", "EE Dashboard");
    can("read", "EE Tasks");
    can("read", "OrdersQueued");
    can("read", "Tasks");
    can("read", "Handheld");
    can("read", "ZF Handheld");
    can("read", "EE Handheld Profiling");
  }
  if (intersection(groups, ["ee-head-production"]).length > 0) {
    can("read", "EE Batches");
    can("read", "EE Dashboard");
    can("read", "EE Tasks");
    can("read", "OrdersQueued");
    can("read", "Tasks");
    can("read", "Handheld");
    can("read", "ZF Handheld");
    can("read", "EE Handheld Profiling");
  }
  if (intersection(groups, ["ee-head-operator"]).length > 0) {
    can("read", "EE Batches");
    can("read", "EE Dashboard");
    can("read", "EE Tasks");
    can("read", "OrdersQueued");
    can("read", "Tasks");
    can("read", "Handheld");
    can("read", "ZF Handheld");
    can("read", "EE Handheld Profiling");
  }
  if (intersection(groups, ["ee-frontman"]).length > 0) {
    can("fullAccess", "EE Batches");
    can("fullAccess", "EE Tasks");
    can("fullAccess", "EE Settings");
    can("fullAccess", "EE Dashboard");
    can("fullAccess", "OrdersQueued");
    can("fullAccess", "Tasks");
    can("fullAccess", "Handheld");
    can("read", "ZF Handheld");
    can("read", "EE Devices");
    can("fullAccess", "EE Handheld Profiling");
  }
  if (intersection(groups, ["ee-operator"]).length > 0) {
    can("read", "EE Dashboard");
    can("read", "EE Batches");
    can("read", "Tasks");
    can("fullAccess", "Handheld");
    can("read", "ZF Handheld");
    can("fullAccess", "EE Handheld Profiling");
  }

  return rules;
}

export function buildAbility(groups: string[]) {
  return new AppAbility(defineRules(groups), { resolveAction });
}
export const TOKEN = ABILITY_TOKEN as InjectionKey<AppAbility>;
